import Analytics from '@withlocals/withlocals-analytics';
import { isExcluded } from 'hoc/ExcludeOnPlatforms';
import Cookies from 'js-cookie';
import React, {
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { WlWindow } from 'typings/globals/cordova';
import { Trek } from 'utils/analytics';

import { CONSENT_COOKIES_KEYS } from '../constants';

interface CookieConsentState {
  base: boolean;
  analytics: boolean;
  advertisement: boolean;
  social: boolean;
  personalised_ads: boolean;
  location: boolean;
}
type CookieConsentContextType = CookieConsentState & {
  setConsent: (state: CookieConsentState) => void;
};
declare const window: WlWindow;

const setCookies = ({
  base,
  advertisement,
  social,
  personalised_ads,
  analytics,
  location,
}: CookieConsentState) => {
  Cookies.set(
    CONSENT_COOKIES_KEYS.CONSENT_SAVED,
    base.toString(),
    DefaultCookieOpts,
  );
  Cookies.set(
    CONSENT_COOKIES_KEYS.ANALYTICS,
    analytics.toString(),
    DefaultCookieOpts,
  );
  Cookies.set(
    CONSENT_COOKIES_KEYS.PERSONALISED_ADS,
    personalised_ads.toString(),
    DefaultCookieOpts,
  );
  Cookies.set(
    CONSENT_COOKIES_KEYS.ADVERTISEMENT,
    advertisement.toString(),
    DefaultCookieOpts,
  );
  Cookies.set(
    CONSENT_COOKIES_KEYS.SOCIAL,
    social.toString(),
    DefaultCookieOpts,
  );
  Cookies.set(
    CONSENT_COOKIES_KEYS.LOCATION,
    location.toString(),
    DefaultCookieOpts,
  );
};
const notifyAnalytics = (settings: CookieConsentState) => {
  // If checks for new analytics lib, can be removed when new version is live.
  if (Analytics.setAdvertisementConsent) {
    Analytics.setAdvertisementConsent(settings.advertisement);
  }
  if (Analytics.setSocialConsent) {
    Analytics.setSocialConsent(settings.social);
  }

  if (settings.advertisement && settings.social) {
    Analytics.track('Cookie Consent', 'Default + advertisement + social');
  } else if (settings.advertisement) {
    Analytics.track('Cookie Consent', 'Default + advertisement');
  } else if (settings.social) {
    Analytics.track('Cookie Consent', 'Default + social');
  } else {
    Analytics.track('Cookie Consent', 'Default');
  }
};

const isConsentGivenOrApp = (name: string) => {
  const isNeeded = !isExcluded(['server']);
  if (isNeeded) {
    return Cookies.get(name) === 'true';
  }
  return false;
};

const getCookieConsent = () => ({
  base: isConsentGivenOrApp(CONSENT_COOKIES_KEYS.CONSENT_SAVED),
  advertisement: isConsentGivenOrApp(CONSENT_COOKIES_KEYS.ADVERTISEMENT),
  analytics: isConsentGivenOrApp(CONSENT_COOKIES_KEYS.ANALYTICS),
  personalised_ads: isConsentGivenOrApp(CONSENT_COOKIES_KEYS.PERSONALISED_ADS),
  social: isConsentGivenOrApp(CONSENT_COOKIES_KEYS.SOCIAL),
  location: isConsentGivenOrApp(CONSENT_COOKIES_KEYS.LOCATION),
});

const CookieConsentContext = React.createContext<CookieConsentContextType>({
  base: false,
  analytics: false,
  personalised_ads: false,
  advertisement: false,
  social: false,
  location: false,
  setConsent: setCookies,
});
export const DefaultCookieOpts = {
  expires: 9999,
};

export function CookieConsentContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [state, setState] = useState<CookieConsentState>(getCookieConsent());
  // this enabled GA to immediately set consent values correct.
  window.consent = state;

  const setConsent = useCallback((settings: CookieConsentState) => {
    setCookies(settings);
    setState(settings);
    notifyAnalytics(settings);
  }, []);
  const context = useMemo(() => {
    return { ...state, setConsent };
  }, [setConsent, state]);
  const trekkerContext = useMemo(() => {
    return {
      cookieConsent: {
        functional: state.base,
        analytics: state.base,
        advertisement: state.advertisement,
        personalised_ads: state.personalised_ads,
        social: state.social,
        location: state.location,
      },
    };
  }, [state]);

  return (
    <CookieConsentContext.Provider value={context}>
      <Trek context={trekkerContext}>{children}</Trek>
    </CookieConsentContext.Provider>
  );
}

export function useConsentSerialized() {
  const { setConsent, ...state } = useCookieConsent();
  const setConcentFromSerialized = useCallback(
    (input: string) => {
      if (input.includes('b')) {
        state.base = true;
      }
      if (input.includes('a')) {
        state.advertisement = true;
      }
      if (input.includes('s')) {
        state.social = true;
      }
      if (input.includes('l')) {
        state.location = true;
      }
      setConsent(state);
    },
    [setConsent, state],
  );

  return { setConcentFromSerialized };
}

export function serializeConsentCookies() {
  const isNeeded = !isExcluded(['server']);
  if (!isNeeded) return 'asb';
  const state = getCookieConsent();
  const serializedConsentState = [
    state.advertisement ? 'a' : null,
    state.location ? 'l' : null,
    state.base ? 'b' : null,
    state.social ? 's' : null,
  ]
    .filter((x) => !!x)
    .join('');
  return serializedConsentState;
}

export function useCookieConsent() {
  return useContext(CookieConsentContext);
}
